import AbstractComponent from "./abstract-component";
import gsap from "gsap";
import { CustomEase } from "gsap/CustomEase";
import { curtainTokens, curtainOpenProps } from "../motion/curtain";
import { preloadRevealAssets } from "./split-type";

gsap.registerPlugin(CustomEase);

/**
 * Loader 1ʳᵉ visite — Lottie jusqu’à la dernière frame, puis rideau
 * (même open que page-transition via motion/curtain.js).
 */

/** Hold min si pas de Lottie / load raté */
const MIN_HOLD_MS = 1200;
/** Filet absolu — ne jamais bloquer le site */
const HARD_CAP_MS = 5500;
const EXIT_FAILSAFE_MS = 3500;
const LOGO_LOAD_MS = 800;
/**
 * Pose sur le logo figé avant clip / rideau.
 */
const LOGO_END_HOLD_MS = 380;
/** Démarrage Lottie tôt — la sortie attend la fin du morph */
const LOTTIE_BOOT_DELAY_MS = 80;
/**
 * Le JSON continue ~1s après le morph (opacité 100 @ ~f76, op @104).
 * On scrub jusqu’à la fin du morph + petite marge, pas jusqu’à op.
 */
const LOTTIE_POST_MORPH_HOLD_FRAMES = 4;
/** Scrub 10 % plus rapide que le framerate JSON */
const LOTTIE_SPEED = 0.9;

const COL_DUR = curtainTokens.colDur;
/** Sortie logo sync rideau — clip haut + slide up */
const LOGO_EXIT_DUR = curtainTokens.colDurOut;
const CONTENT_REVEAL_AT = curtainTokens.revealAt;
const LOTTIE_COMP_W = 402;
const LOTTIE_COMP_H = 263;
/**
 * Crop wordmark dès le boot — mêmes dims jusqu’au clip-path.
 * (Pas de resize mid-flight = plus de « gros d’un coup ».)
 */
const LOTTIE_VIEW = Object.freeze({ x: 6, y: 86, width: 390, height: 92 });

const EASE_LOGO_IN = CustomEase.create("drieuxLogoIn", "0.34,1,0.14,1");
const EASE_LOGO_HOLD = CustomEase.create("drieuxLogoHold", "0.86,0.86,0.167,0.167");

/** Dernière keyframe animée du JSON (fin du morph logo). */
function maxLottieKeyframeTime(data) {
	let max = 0;
	const walk = (node) => {
		if (!node || typeof node !== "object") return;
		if (Array.isArray(node)) {
			node.forEach(walk);
			return;
		}
		if (node.a === 1 && Array.isArray(node.k) && node.k.length && node.k[0]?.t != null) {
			for (const k of node.k) {
				if (typeof k.t === "number" && k.t > max) max = k.t;
			}
		}
		for (const key of Object.keys(node)) {
			if (key === "assets") continue;
			walk(node[key]);
		}
	};
	walk(data?.layers);
	return max;
}

/*
 * Loader joué 1× par session (onglet) : flag sessionStorage lu par le script inline
 * de site-loader.twig, qui masque le loader avant le 1er paint sur les visites suivantes.
 */
const LOADER_SEEN_KEY = "drieux_loader_seen";

function rememberLoaderSeen() {
	try {
		window.sessionStorage.setItem(LOADER_SEEN_KEY, "1");
	} catch {
		/* storage indisponible (navigation privée stricte) : loader rejoué, sans gravité */
	}
}

export default class SiteLoader extends AbstractComponent {
	constructor(el) {
		super(el);
		this.done = false;
		this._exiting = false;
		this._exitTl = null;
		this._logoAnim = null;
		this._logoFrames = null;
		this._lottieDone = false;
		this._timers = [];
		this.initDomElements();
		this.init();
	}

	initDomElements() {
		this.DOM = {
			brand: this.$el.querySelector(".c-site-loader__brand"),
			logoBox: this.$el.querySelector(".c-site-loader__logo"),
			logoStage: this.$el.querySelector(".c-site-loader__logo-stage[data-lottie]"),
			columns: [...this.$el.querySelectorAll(".c-site-loader__column")],
			veil: this.$el.querySelector(".c-site-loader__veil"),
		};
	}

	get isRecordMode() {
		try {
			return new URLSearchParams(window.location.search).has("loader-record");
		} catch {
			return false;
		}
	}

	_arm(ms, fn) {
		const id = setTimeout(fn, ms);
		this._timers.push(id);
		return id;
	}

	_clearTimers() {
		this._timers.forEach((id) => clearTimeout(id));
		this._timers = [];
	}

	init() {
		if (this.$el.classList.contains("loaded") || window.__drieuxLoaderDone) {
			this.done = true;
			this.$el.classList.add("loaded");
			this.$el.setAttribute("aria-busy", "false");
			return;
		}

		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches && !this.isRecordMode) {
			this.finishImmediate();
			return;
		}

		this.paintCover();
		preloadRevealAssets();

		if (!this.isRecordMode) {
			/* Filet uniquement — la sortie normale = fin Lottie */
			this._arm(HARD_CAP_MS, () => this.finishImmediate());
			/* Fallback SEULEMENT si Lottie n’a jamais démarré (ne pas couper un scrub en cours) */
			this._arm(MIN_HOLD_MS + LOGO_LOAD_MS, () => {
				if (this._lottieDone || this._exiting || this.done) return;
				if (this._logoAnim && this._logoFrames) return;
				this.exit();
			});
		}

		const lottieDelay = this.isRecordMode ? 0 : LOTTIE_BOOT_DELAY_MS;
		this._arm(lottieDelay, () => this.bootLottie());
	}

	paintCover() {
		const gsap = this.app?.gsap;
		const { logoBox, columns, veil } = this.DOM;
		this.$el.classList.remove("is-logo-playing", "is-cols-out", "loaded");

		if (!gsap) return;

		if (columns?.length) {
			gsap.set(columns, { autoAlpha: 1, yPercent: 0, force3D: true });
		}
		if (veil) gsap.set(veil, { autoAlpha: 1 });
		if (logoBox) {
			gsap.set(logoBox, {
				y: 0,
				clipPath: "inset(0% 0% 0% 0%)",
				webkitClipPath: "inset(0% 0% 0% 0%)",
			});
		}
	}

	async bootLottie() {
		const { logoBox, logoStage } = this.DOM;
		if (!logoBox || !logoStage) {
			this._onLottieComplete();
			return;
		}

		const url = logoStage.getAttribute("data-lottie");
		if (!url) {
			this._onLottieComplete();
			return;
		}

		try {
			await Promise.race([this.loadLogoLottie(logoStage, url), this._wait(LOGO_LOAD_MS)]);
		} catch {
			this._onLottieComplete();
			return;
		}

		if (this.done || this._exiting) return;

		if (!this._logoAnim || !this._logoFrames) {
			this._onLottieComplete();
			return;
		}

		try {
			await this.applyLogoFit(logoBox, logoStage);
			this.$el.classList.add("is-logo-playing");
			this.playLottieToEnd();
		} catch {
			this.$el.classList.add("is-logo-playing");
			this._onLottieComplete();
		}
	}

	_wait(ms) {
		return new Promise((r) => this._arm(ms, r));
	}

	loadLogoLottie(logoStage, lottieUrl) {
		return new Promise((resolve) => {
			let settled = false;
			const done = () => {
				if (settled) return;
				settled = true;
				resolve();
			};

			import("lottie-web")
				.then((mod) => {
					if (this.done || this._exiting) {
						done();
						return;
					}
					const Lottie = mod.default || mod;
					try {
						this._logoAnim = Lottie.loadAnimation({
							container: logoStage,
							renderer: "svg",
							loop: false,
							autoplay: false,
							path: lottieUrl,
							rendererSettings: {
								progressiveLoad: false,
								hideOnTransparent: true,
								preserveAspectRatio: "xMidYMid meet",
							},
						});

						const onReady = () => {
							try {
								const data = this._logoAnim.animationData ?? {};
								const frameRate = data.fr ?? this._logoAnim.frameRate ?? 30;
								const inFrame = Math.round(data.ip ?? this._logoAnim.inPoint ?? 0);
								const compOut = Math.max(
									inFrame + 1,
									Math.round((data.op ?? this._logoAnim.outPoint ?? this._logoAnim.totalFrames ?? 1) - 1)
								);
								const morphEnd = Math.round(maxLottieKeyframeTime(data) || compOut);
								/* Hold frame = fin morph (+ marge), pas la frame morte en fin de compo */
								const holdFrame = Math.min(
									compOut,
									Math.max(inFrame + 1, morphEnd + LOTTIE_POST_MORPH_HOLD_FRAMES)
								);
								this._logoFrames = { inFrame, outFrame: holdFrame, compOut, morphEnd, frameRate };
								this._logoAnim.goToAndStop(inFrame, true);
								done();
							} catch {
								done();
							}
						};

						this._logoAnim.addEventListener("DOMLoaded", onReady);
						this._logoAnim.addEventListener("data_ready", onReady);
						this._arm(LOGO_LOAD_MS, done);
					} catch {
						done();
					}
				})
				.catch(() => done());
		});
	}

	/**
	 * Scrub rAF à vitesse réelle jusqu’à la fin du morph (pas jusqu’à op).
	 * Puis pose figée → clip-path slide-up + rideau.
	 */
	playLottieToEnd() {
		if (!this._logoAnim || !this._logoFrames || this.done || this._exiting) {
			this._onLottieComplete();
			return;
		}

		const { inFrame, outFrame, frameRate } = this._logoFrames;
		const span = Math.max(1, outFrame - inFrame);
		/* −10 % vs durée réelle du morph */
		const durMs = Math.max(720, ((span / (frameRate || 30)) * 1000) * LOTTIE_SPEED);
		const t0 = performance.now();

		/*
		 * Pas de anim.play() : morph SVG haute cadence peut geler le main thread.
		 * Scrub goToAndStop via rAF = 1 frame / tick.
		 */
		const tick = (now) => {
			if (this.done || this._exiting || !this._logoAnim) return;
			const p = Math.min(1, (now - t0) / durMs);
			const frame = inFrame + span * p;
			try {
				this._logoAnim.goToAndStop(frame, true);
			} catch {
				this._onLottieComplete();
				return;
			}
			if (p < 1) {
				requestAnimationFrame(tick);
				return;
			}
			this.snapLogoEnd();
			/* Pose figée — dims déjà bonnes depuis le boot, pas de reflow */
			this._arm(LOGO_END_HOLD_MS, () => this._onLottieComplete());
		};

		try {
			this._logoAnim.goToAndStop(inFrame, true);
			requestAnimationFrame(tick);
		} catch {
			this._onLottieComplete();
		}
	}

	_onLottieComplete() {
		if (this._lottieDone || this.done || this._exiting) return;
		this._lottieDone = true;
		if (this.isRecordMode) return;
		this.exit();
	}

	snapLogoEnd() {
		try {
			if (this._logoAnim && this._logoFrames) {
				this._logoAnim.pause();
				this._logoAnim.goToAndStop(this._logoFrames.outFrame, true);
			}
		} catch {
			/* ignore */
		}
	}

	killLottie() {
		try {
			this._logoAnim?.destroy?.();
		} catch {
			/* ignore */
		}
		this._logoAnim = null;
		this._logoFrames = null;
	}

	async applyLogoFit(logoBox, logoStage, crop = LOTTIE_VIEW) {
		if (!logoStage || !logoBox || !crop) return;
		const view = {
			x: crop.x || 0,
			y: crop.y || 0,
			width: Math.max(1, crop.width || LOTTIE_COMP_W),
			height: Math.max(1, crop.height || LOTTIE_COMP_H),
		};
		/* Largeur CSS inchangée — hauteur = crop JSON dès le 1er paint */
		logoBox.style.width = "";
		logoBox.style.aspectRatio = `${view.width} / ${view.height}`;
		logoBox.style.height = "";
		await new Promise((r) => requestAnimationFrame(r));
		const boxW = logoBox.clientWidth || logoBox.getBoundingClientRect().width || 308;
		const scale = boxW / view.width;
		logoStage.style.transformOrigin = "0 0";
		logoStage.style.transform = `translate(${-view.x * scale}px, ${-view.y * scale}px) scale(${scale})`;
	}

	async exit() {
		if (this.done || this._exiting) return;
		this._exiting = true;
		this.snapLogoEnd();

		const gsap = this.app?.gsap;
		if (!gsap) {
			this.finishImmediate();
			return;
		}

		const { brand, logoBox, columns, veil } = this.DOM;
		this.$el.classList.add("transiting", "is-logo-exiting");
		this.$el.setAttribute("aria-busy", "false");

		const markReveal = () => {
			if (window.__drieuxLoaderReveal) return;
			window.__drieuxLoaderReveal = true;
			/* Contenu visible → ne plus bloquer les touches (scroll mobile) */
			this.$el.style.pointerEvents = "none";
			this.$el.classList.add("is-content-revealed");
			window.dispatchEvent(new CustomEvent("site-loader:reveal"));
		};

		const markDone = () => {
			if (window.__drieuxLoaderDone) return;
			this.done = true;
			this._clearTimers();
			this.killLottie();
			this.$el.classList.remove("is-logo-playing", "is-logo-exiting", "is-cols-out", "transiting");
			this.$el.classList.add("loaded");
			window.__drieuxLoaderDone = true;
			rememberLoaderSeen();
			window.dispatchEvent(new CustomEvent("site-loader:done"));
		};

		this._arm(EXIT_FAILSAFE_MS, markDone);

		try {
			this._exitTl?.kill?.();
			this._exitTl = gsap.timeline({ onComplete: markDone });

			/*
			 * Pause Lottie → clip-path + slide-up (dims inchangées depuis le boot).
			 */
			if (brand) {
				gsap.set(brand, { autoAlpha: 1, overflow: "hidden" });
			}
			if (logoBox) {
				gsap.set(logoBox, {
					autoAlpha: 1,
					visibility: "visible",
					opacity: 1,
					yPercent: 0,
					y: 0,
					clipPath: "inset(0% 0% 0% 0%)",
					webkitClipPath: "inset(0% 0% 0% 0%)",
					overflow: "hidden",
					force3D: true,
				});
				this._exitTl.fromTo(
					logoBox,
					{
						yPercent: 0,
						clipPath: "inset(0% 0% 0% 0%)",
						webkitClipPath: "inset(0% 0% 0% 0%)",
					},
					{
						yPercent: -100,
						clipPath: "inset(100% 0% 0% 0%)",
						webkitClipPath: "inset(100% 0% 0% 0%)",
						duration: LOGO_EXIT_DUR,
						ease: EASE_LOGO_IN,
						force3D: true,
					},
					0
				);
			}

			if (brand) {
				this._exitTl.to(
					brand,
					{ autoAlpha: 0, duration: 0.28, ease: EASE_LOGO_HOLD },
					Math.max(0, LOGO_EXIT_DUR - 0.28)
				);
			}

			if (veil) {
				this._exitTl.to(veil, { autoAlpha: 0, duration: 0.35, ease: "power2.out" }, 0.35);
			}

			if (columns?.length) {
				this._exitTl.to(columns, curtainOpenProps(), 0);
			}

			this._exitTl.add(() => this.$el.classList.add("is-cols-out"), COL_DUR);
			this._exitTl.add(markReveal, CONTENT_REVEAL_AT);
		} catch {
			markDone();
		}
	}

	finishImmediate() {
		if (window.__drieuxLoaderDone && this.$el.classList.contains("loaded")) return;
		this.done = true;
		this._exiting = true;
		this._clearTimers();
		this._exitTl?.kill?.();
		this.killLottie();
		this.$el.classList.remove("is-logo-playing", "is-logo-exiting", "is-cols-out", "transiting");
		this.$el.classList.add("loaded");
		this.$el.setAttribute("aria-busy", "false");
		window.__drieuxLoaderReveal = true;
		window.__drieuxLoaderDone = true;
		rememberLoaderSeen();
		window.dispatchEvent(new CustomEvent("site-loader:reveal"));
		window.dispatchEvent(new CustomEvent("site-loader:done"));
	}

	destroy() {
		/* hors Barba */
	}
}
